import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { bagKeys } from './bag';
import { walletKeys, type WalletDto } from './wallet';
import { petSlotsKeys } from './petSlots';

export type CatalogEntryDto = {
  sku: string;
  title: string;
  entryType: string;
  currency: string;
  price: number;
};

/** `GET /catalog` is a bare array (`CatalogController.list()` returns `List<CatalogEntryDto>`),
 *  not an `{ entries }` envelope. The envelope shape shipped here for weeks and crashed `SlotsRow`
 *  (and with it the whole Me screen) for every keeper past level 20 — the first account state
 *  that ever read it (QA 2026-09-23, blocker B1). */
export type CatalogDto = CatalogEntryDto[];

export const catalogKeys = {
  all: ['catalog'] as const,
};

export const getCatalog = () => apiFetch<CatalogDto>('/catalog');

export function useCatalog() {
  return useQuery({ queryKey: catalogKeys.all, queryFn: getCatalog });
}

export type CatalogGrantedDto = {
  kind: string;
  itemCode: string;
  qty: number;
  currency: string;
  amount: number;
};

export type CatalogPurchaseResultDto = {
  wallet: WalletDto;
  granted: CatalogGrantedDto[];
};

export const purchaseCatalogEntry = (sku: string, idempotencyKey: string) =>
  apiFetch<CatalogPurchaseResultDto>(`/catalog/${sku}/purchase`, {
    method: 'POST',
    body: JSON.stringify({ idempotencyKey }),
  });

/** NOT optimistic, same reasoning as usePurchase in shops.ts — writes the server's authoritative
 *  wallet directly into the cache and invalidates the bag (a granted item or bundle contents may
 *  have landed there). idempotencyKey generated fresh per mutate() call.
 *
 *  Phase 4 Task 15 fix: also invalidates `petSlotsKeys.all` — a `slot_4`/`slot_5`/`slot_6` SKU
 *  purchase is the ONLY way a locked pet slot ever unlocks past the two free level thresholds
 *  (PetSlotReward, petSlots.ts's own header comment), and nothing was invalidating SlotsRow's
 *  usePetSlots() cache on this path before, so a successful purchase left "Companions N/6" and
 *  the purchase CTA both stale until something unrelated happened to refetch it. Harmless no-op
 *  for every other SKU (bundles, cosmetics, ...), which don't touch slot capacity at all. */
export function useCatalogPurchase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sku: string) => purchaseCatalogEntry(sku, crypto.randomUUID()),
    onSuccess: (data) => {
      queryClient.setQueryData(walletKeys.all, data.wallet);
      queryClient.invalidateQueries({ queryKey: bagKeys.all });
      queryClient.invalidateQueries({ queryKey: petSlotsKeys.all });
    },
  });
}
